import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import {type AccountMismatchIdentities, type ProfileIdentity} from '@perfectpost/perfect-post-common';
import {useTranslation} from 'react-i18next';

/**
 * Les deux profils en conflit, affichés à l'identique partout où le web signale un
 * `ACCOUNT_MISMATCH` (bandeau de session, checklist d'onboarding).
 *
 * Côté PerfectPost, le lien est bâti sur l'`entityUrn` enregistré — le champ qui a servi à la
 * décision. `https://www.linkedin.com/in/<entityUrn>` résout : l'utilisateur clique et voit de ses
 * yeux le profil réellement associé à son compte, là où l'ancien affichage montrait un
 * `vanityName` souvent identique des deux côtés.
 *
 * Les valeurs brutes sont rendues sous le lien : personne ne saura les interpréter, mais elles
 * sont recopiables dans un message au support.
 */

const RawValue = ({field, value}: {field: 'entityUrn' | 'vanityName'; value?: string}) => {
  const {t} = useTranslation();
  return (
    <Typography variant="caption" sx={{display: 'block', fontFamily: 'monospace', wordBreak: 'break-all'}}>
      {field} : {value ?? t('notification.debug.not_provided')}
    </Typography>
  );
};

const Profile = ({label, identity}: {label: string; identity: ProfileIdentity}) => {
  const {t} = useTranslation();
  return (
    <Box sx={{mb: 1.5}}>
      <Typography variant="body2" fontWeight="bold" sx={{mb: 0.5}}>
        {label}
      </Typography>
      {identity.url === undefined ? (
        <Typography variant="body2">{t('notification.debug.not_provided')}</Typography>
      ) : (
        <Link
          href={identity.url}
          target="_blank"
          rel="noopener noreferrer"
          variant="body2"
          sx={{wordBreak: 'break-all'}}
        >
          linkedin.com/in/{identity.identifier}
        </Link>
      )}
      <RawValue field="entityUrn" value={identity.entityUrn} />
      <RawValue field="vanityName" value={identity.vanityName} />
    </Box>
  );
};

export const AccountMismatchProfiles = ({
  identities,
  registeredLabel,
  currentLabel,
}: {
  identities: AccountMismatchIdentities;
  registeredLabel: string;
  currentLabel: string;
}) => {
  const {t} = useTranslation();
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{display: 'block', mb: 1}}>
        {t('notification.debug.technical_ids')}
      </Typography>
      <Profile label={registeredLabel} identity={identities.registered} />
      <Profile label={currentLabel} identity={identities.current} />
    </Box>
  );
};
